import { toPng } from 'html-to-image';
import { getNodesBounds, getViewportForBounds, type ReactFlowInstance } from 'reactflow';

const sanitizeFileName = (value: string) =>
  value.replace(/[<>:"/\\|?*]+/g, '_').replace(/\s+/g, '_').slice(0, 80);

const EXCLUDE_EXPORT_CLASSES = [
  'react-flow__minimap',
  'react-flow__controls',
  'react-flow__panel',
];

const waitForPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

/**
 * Exporta o diagrama como PNG (padrão oficial do React Flow).
 * @see https://reactflow.dev/examples/misc/download-image
 */
export async function exportDiagramImage(
  container: HTMLElement,
  reactFlow: ReactFlowInstance,
  title: string,
): Promise<void> {
  const nodes = reactFlow.getNodes();
  if (nodes.length === 0) {
    throw new Error('Diagrama vazio');
  }

  const viewportEl = container.querySelector('.react-flow__viewport') as HTMLElement | null;
  if (!viewportEl) {
    throw new Error('Área do diagrama não encontrada');
  }

  await waitForPaint();

  const padding = 48;
  const nodesBounds = getNodesBounds(nodes);
  const imageWidth = Math.max(Math.ceil(nodesBounds.width) + padding * 2, 1200);
  const imageHeight = Math.max(Math.ceil(nodesBounds.height) + padding * 2, 800);

  const viewport = getViewportForBounds(
    nodesBounds,
    imageWidth,
    imageHeight,
    0.5,
    2,
    padding,
  );

  const dataUrl = await toPng(viewportEl, {
    backgroundColor: '#ffffff',
    width: imageWidth,
    height: imageHeight,
    pixelRatio: 2,
    cacheBust: true,
    style: {
      width: `${imageWidth}px`,
      height: `${imageHeight}px`,
      transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
    },
    filter: (node) => {
      if (!(node instanceof HTMLElement)) return true;
      return !EXCLUDE_EXPORT_CLASSES.some((className) => node.classList?.contains(className));
    },
  });

  const link = document.createElement('a');
  link.download = `${sanitizeFileName(title)}.png`;
  link.href = dataUrl;
  link.click();
}
